import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';

import { PlaceholderSlot } from '@sorare/marketplace/src/components/commonCard/RevealPackCardsDialog/CollectionAnimation/CollectionAnimationClub/PlaceholderSlot';

import { CardsCountBadge } from 'components/collections/CardsCountBadge';

import type { CollectionSlot_userCardCollectionSlot } from './__generated__/index.graphql';

const Root = styled.div`
  cursor: pointer;
  position: relative;
  isolation: isolate;
  border: 1px solid var(--c-nd-50);
  border-radius: var(--double-unit);
  overflow: hidden;
  aspect-ratio: var(--card-aspect-ratio);
`;

type Props = {
  userCardCollectionSlot: Nullable<CollectionSlot_userCardCollectionSlot>;
  small?: boolean;
  rarity: Nullable<Rarity>;
};

export const CollectionSlot = ({
  userCardCollectionSlot,
  small = false,
  rarity,
}: Props) => {
  const { highlightedCardCollectionCard, cardCollectionCardsCount } =
    userCardCollectionSlot || {};
  const { anyCard } = highlightedCardCollectionCard || {};
  const isLaptopAndAbove = useIsLaptop();
  const openDialog = useOpenDialog();
  if (cardCollectionCardsCount === 0 || !anyCard) {
    const slotId = userCardCollectionSlot?.slot.id;
    return (
      <ConditionalWrapper
        Wrapper={Root}
        props={{
          onClick: () => openDialog(DialogKey.slot, slotId!),
        }}
        wrap={slotId !== undefined}
      >
        <PlaceholderSlot slot={userCardCollectionSlot} small={small} />
      </ConditionalWrapper>
    );
  }
  return (
    <CardsCountBadge
      size="small"
      userCardCollectionSlot={userCardCollectionSlot}
      rarity={rarity}
    >
      <ButtonBase onClick={() => openDialog(DialogKey.card, anyCard.slug)}>
        <CardImg
          src={anyCard.pictureUrl}
          alt=""
          width={isLaptopAndAbove ? 160 : 80}
        />
      </ButtonBase>
    </CardsCountBadge>
  );
};

CollectionSlot.fragments = {
  userCardCollectionSlot: gql`
    fragment CollectionSlot_userCardCollectionSlot on UserCardCollectionSlot {
      slug
      slot {
        id
      }
      cardCollectionCardsCount
      highlightedCardCollectionCard {
        id
        anyCard {
          slug
          pictureUrl
        }
      }
      ...PlaceholderSlot_userCardCollectionSlot
      ...CardsCountBadge_userCardCollectionSlot
    }
    ${PlaceholderSlot.fragments.userCardCollectionSlot}
    ${CardsCountBadge.fragments.userCardCollectionSlot}
  ` as TypedDocumentNode<CollectionSlot_userCardCollectionSlot>,
};
